import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (response.data.user) {
      setUser(response.data.user);
    }
    return response.data;
  };

  const register = async (name, email, phone, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, phone, password });
    return response.data;
  };

  const verifyEmail = async (email, otp) => {
    const response = await axios.post(`${API}/auth/verify-email`, { email, otp }, { withCredentials: true });
    if (response.data.user) {
      setUser(response.data.user);
    }
    return response.data;
  };

  const verify2FA = async (email, otp) => {
    const response = await axios.post(`${API}/auth/verify-2fa`, { email, otp }, { withCredentials: true });
    if (response.data.user) {
      setUser(response.data.user);
    }
    return response.data;
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API}/auth/forgot-password`, { email });
    return response.data;
  };

  const resetPassword = async (email, otp, newPassword) => {
    const response = await axios.post(`${API}/auth/reset-password`, { email, otp, new_password: newPassword });
    return response.data;
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId) => {
    const response = await axios.post(`${API}/auth/google/callback`, { session_id: sessionId }, { withCredentials: true });
    if (response.data.user) {
      setUser(response.data.user);
    }
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  const toggle2FA = async (enabled) => {
    const response = await axios.post(`${API}/auth/toggle-2fa`, { enabled }, { withCredentials: true });
    if (user) {
      setUser({ ...user, two_factor_enabled: enabled });
    }
    return response.data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      verifyEmail,
      verify2FA,
      forgotPassword,
      resetPassword,
      googleLogin,
      handleGoogleCallback,
      logout,
      toggle2FA,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
